import { useRef, useState, useCallback, useEffect, Suspense } from 'react';
import { Canvas } from '@react-three/fiber';
import { EffectComposer, Bloom } from '@react-three/postprocessing';
import * as THREE from 'three';
import { useGameStore } from '@/hooks/useGameState';
import { useDebugStore } from '@/hooks/useDebugEvents';
import { useAudioManager } from '@/hooks/useAudio';
import { useHighScore } from '@/hooks/useHighScore';
import { Analytics } from '@/lib/analytics';
import { QUALITY_SETTINGS } from '@/types/game';
import { Snake } from './Snake';
import { Food } from './Food';
import { Comet } from './Comet';
import { Starfield } from './Starfield';
import { WorldBoundary } from './WorldBoundary';
import { PlayerController } from './PlayerController';
import { GameLogic } from './GameLogic';
import { MainMenu } from '@/components/ui/MainMenu';
import { HUD } from '@/components/ui/HUD';
import { DeathScreen } from '@/components/ui/DeathScreen';
import { CometNotification } from '@/components/ui/CometNotification';
import { DebugAIEatNotification } from '@/components/ui/DebugAIEatNotification';
import { Minimap } from '@/components/ui/Minimap';
import { ErrorBoundary } from '@/components/ui/ErrorBoundary';

const GameScene = () => {
  const playerSnake = useGameStore((state) => state.playerSnake);
  const aiSnakes = useGameStore((state) => state.aiSnakes);
  const food = useGameStore((state) => state.food);
  const comets = useGameStore((state) => state.comets);
  const quality = useGameStore((state) => state.quality);
  const qualitySettings = QUALITY_SETTINGS[quality];

  const { playEatSound, playDeathSound, playKillSound, playCometSound, playBoostSound } = useAudioManager();
  
  const [playerPosition, setPlayerPosition] = useState(new THREE.Vector3());
  const [playerQuaternion, setPlayerQuaternion] = useState(new THREE.Quaternion());

  const handlePositionUpdate = useCallback((pos: THREE.Vector3, quat: THREE.Quaternion) => {
    setPlayerPosition(pos.clone());
    setPlayerQuaternion(quat.clone());
  }, []);

  const handleEatFood = useCallback(() => {
    playEatSound();
  }, [playEatSound]);

  const handleEatComet = useCallback(() => {
    playCometSound();
  }, [playCometSound]);

  const handleDeath = useCallback(() => {
    playDeathSound();
  }, [playDeathSound]);

  const handleKillSnake = useCallback((snakeId: string) => {
    playKillSound();
  }, [playKillSound]);

  const handleBoostStart = useCallback(() => {
    playBoostSound(true);
  }, [playBoostSound]);

  const handleBoostStop = useCallback(() => {
    playBoostSound(false);
  }, [playBoostSound]);

  return (
    <>
      {/* Ambient light for base visibility */}
      <ambientLight intensity={0.3} />
      
      {/* Point lights for neon effect */}
      <pointLight position={[0, 100, 0]} intensity={0.5} color="#00FFFF" />
      <pointLight position={[100, 0, 0]} intensity={0.3} color="#FF00FF" />
      <pointLight position={[-100, 0, 0]} intensity={0.3} color="#00FF00" />
      
      {/* Background */}
      <color attach="background" args={['#050505']} />
      <fog attach="fog" args={['#050505', 100, qualitySettings.fogFar]} />
      
      {/* Starfield */}
      <Starfield 
        playerPosition={playerSnake?.position || new THREE.Vector3()} 
        velocity={playerSnake?.velocity || 50} 
      />
      
      {/* World boundaries */}
      <WorldBoundary />
      
      {/* Food orbs */}
      {food.length > 0 && <Food food={food} />}
      
      {/* Comets */}
      {comets.length > 0 && <Comet comets={comets} />}
      
      {/* Player snake */}
      {playerSnake && playerSnake.isAlive && (
        <Snake snake={playerSnake} isPlayer />
      )}
      
      {/* AI snakes */}
      {aiSnakes.map((snake) => (
        snake.isAlive && <Snake key={snake.id} snake={snake} />
      ))}
      
      {/* Player controller */}
      {playerSnake && playerSnake.isAlive && (
        <PlayerController
          onPositionUpdate={handlePositionUpdate}
          onBoostStart={handleBoostStart}
          onBoostStop={handleBoostStop}
        />
      )}
      
      {/* Game logic (collision detection, AI updates) */}
      {playerSnake && playerSnake.isAlive && (
        <GameLogic
          playerPosition={playerPosition}
          playerQuaternion={playerQuaternion}
          onEatFood={handleEatFood}
          onEatComet={handleEatComet}
          onDeath={handleDeath}
          onKillSnake={handleKillSnake}
        />
      )}
      
      {/* Post-processing - Bloom for neon glow (only on high quality) */}
      {qualitySettings.bloomEnabled && (
        <EffectComposer>
          <Bloom
            luminanceThreshold={0.85}
            luminanceSmoothing={0.9}
            intensity={1.5}
            radius={0.4}
          />
        </EffectComposer>
      )}
    </>
  );
};

export const GameCanvas = () => {
  const isPlaying = useGameStore((state) => state.isPlaying);
  const playerSnake = useGameStore((state) => state.playerSnake);
  const cometCatcher = useGameStore((state) => state.cometCatcher);
  const clearCometCatcher = useGameStore((state) => state.clearCometCatcher);
  const startGame = useGameStore((state) => state.startGame);
  const respawn = useGameStore((state) => state.respawn);
  const revive = useGameStore((state) => state.revive);
  const quality = useGameStore((state) => state.quality);
  const qualitySettings = QUALITY_SETTINGS[quality];

  // Debug state
  const aiEatEvents = useDebugStore((state) => state.aiEatEvents);
  const removeAIEatEvent = useDebugStore((state) => state.removeAIEatEvent);

  const { initialize, startBGM, stopBGM, playDeathSound } = useAudioManager();
  const { startSession, updateSession } = useHighScore();

  // Periodically save score during gameplay (every 3 seconds)
  const scoreRef = useRef(0);
  scoreRef.current = playerSnake?.score ?? 0;

  useEffect(() => {
    if (!isPlaying || !playerSnake?.isAlive) return;

    // Save immediately when game starts
    updateSession(scoreRef.current);

    // Then save every 3 seconds
    const interval = setInterval(() => {
      updateSession(scoreRef.current);
    }, 3000);

    return () => clearInterval(interval);
  }, [isPlaying, playerSnake?.isAlive, updateSession]);

  const handleStartGame = useCallback(async (name: string, mode: 'single' | 'multi') => {
    await initialize();
    startSession(name);
    startGame(name);
    startBGM();
    Analytics.gameStart(name, mode);
    console.log(`Starting game in ${mode} mode`);
  }, [initialize, startSession, startGame, startBGM]);

  const handleRevive = useCallback(() => {
    revive();
    startBGM();
  }, [revive, startBGM]);

  const handleRespawn = useCallback(() => {
    respawn();
    startBGM();
  }, [respawn, startBGM]);

  // Stop BGM when game ends
  useEffect(() => {
    if (!isPlaying && playerSnake) {
      stopBGM();
    }
  }, [isPlaying, playerSnake, stopBGM]);

  const showDeathScreen = playerSnake && !playerSnake.isAlive;
  const showMenu = !isPlaying && !showDeathScreen;

  return (
    <div className="fixed inset-0 w-full h-full overflow-hidden">
      {/* 3D Canvas - Fullscreen Background */}
      <div className="absolute inset-0 w-full h-full">
        <ErrorBoundary>
          <Canvas
            camera={{ position: [0, 10, -30], fov: 75, near: 0.1, far: 5000 }}
            dpr={qualitySettings.dpr}
            gl={{ antialias: qualitySettings.antialias, alpha: false }}
            style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }}
          >
            <Suspense fallback={null}>
              <GameScene />
            </Suspense>
          </Canvas>
        </ErrorBoundary>
      </div>
      
      {/* UI Overlays - On top of canvas */}
      {showMenu && <MainMenu onStartGame={handleStartGame} />}
      {isPlaying && <HUD />}
      {isPlaying && <Minimap />}
      {showDeathScreen && (
        <DeathScreen onRevive={handleRevive} onRespawn={handleRespawn} />
      )}
      
      {/* Comet catch notification */}
      <CometNotification catcherName={cometCatcher} onDismiss={clearCometCatcher} />
      
      {/* Debug: AI eating notifications */}
      <DebugAIEatNotification events={aiEatEvents} onClearEvent={removeAIEatEvent} />
    </div>
  );
};
